import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsTransport } from '../hooks/useIsTransport';
import { saveTemplate } from './TemplatesPage';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../api/invoices';
import { partiesApi } from '../api/parties';
import { productsApi } from '../api/products';
import { purchasesApi } from '../api/purchases';
import { companyApi } from '../api/company';
import type { Invoice, PaymentMode, Product } from '@coreledger/shared-types';
import { autoTaxType, calcItemTotals } from '@coreledger/business-logic';
import { confirm } from '../components/ConfirmDialog';
import { subscriptionApi } from '../api/subscription';
import { PartySelector } from '../components/invoice/PartySelector';
import { InvoiceItemsTable, recalcTotals } from '../components/invoice/InvoiceItemsTable';
import { TripsEditor } from '../components/invoice/TripsEditor';
import { LrImportModal } from '../components/invoice/LrImportModal';
import { ProductPickerModal } from '../components/invoice/ProductPickerModal';

const TDS_SECTIONS: { section: string; label: string; rate: number }[] = [
  { section: '192',            label: '192 — Salaries',                                     rate: 0    },
  { section: '193',            label: '193 — Interest on Securities',                        rate: 10   },
  { section: '194',            label: '194 — Dividends',                                     rate: 10   },
  { section: '393(1)(ca)',     label: '393(1)(ca) — Interest [prev. 194A]',                  rate: 10   },
  { section: '393(1)(f)',      label: '393(1)(f) — Contractors [prev. 194C]',                rate: 2    },
  { section: '393(1)(g)',      label: '393(1)(g) — Insurance Commission [prev. 194D]',       rate: 5    },
  { section: '393(1)(j)',      label: '393(1)(j) — Commission on Lottery [prev. 194G]',      rate: 5    },
  { section: '393(1)(k)',      label: '393(1)(k) — Commission / Brokerage [prev. 194H]',     rate: 5    },
  { section: '393(1)(l)(i)',   label: '393(1)(l)(i) — Rent: Machinery/Equipment [prev. 194I]', rate: 2 },
  { section: '393(1)(l)(ii)',  label: '393(1)(l)(ii) — Rent: Land / Building [prev. 194I]', rate: 10   },
  { section: '393(1)(m)',      label: '393(1)(m) — Immovable Property Transfer [prev. 194IA]', rate: 1 },
  { section: '393(1)(n)',      label: '393(1)(n) — Professional / Technical Services [prev. 194J]', rate: 10 },
  { section: '393(1)(o)',      label: '393(1)(o) — E-Commerce / Online Ads [prev. 194O]',   rate: 1    },
  { section: '393(1)(p)',      label: '393(1)(p) — Purchase of Goods [prev. 194Q]',         rate: 0.1  },
  { section: '393(1)(s)',      label: '393(1)(s) — Cash Withdrawal [prev. 194N]',           rate: 2    },
  { section: '195',            label: '195 — Payments to Non-Residents',                    rate: 20   },
];
const PAYMENT_MODES: PaymentMode[] = ['cash', 'upi', 'bank_transfer', 'cheque', 'other'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh'];
const today = () => new Date().toISOString().slice(0, 10);

export default function InvoiceFormPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const isEdit = !!id;
  const isTransport = useIsTransport();
  const urlDocType = searchParams.get('docType') as Invoice['docType'] | null;
  const urlPartyId = searchParams.get('partyId');
  const urlPartyName = searchParams.get('partyName');
  const urlLrRefs = searchParams.get('lrRefs');

  const templateData = (() => {
    try { const d = sessionStorage.getItem('cl_use_template'); sessionStorage.removeItem('cl_use_template'); return d ? JSON.parse(d) as Partial<Invoice> : null; }
    catch { return null; }
  })();

  const { data: existing, isLoading: loadingInv } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id!),
    enabled: isEdit,
    staleTime: 0,
  });
  const { data: sub } = useQuery({ queryKey: ['subscription'], queryFn: subscriptionApi.get, staleTime: 60_000 });
  const trialDocTypes = sub?.plan === 'TRIAL' ? new Set(['tax_invoice', 'bill_of_supply']) : null;
  const { data: partiesResult } = useQuery({ queryKey: ['parties'], queryFn: () => partiesApi.list(undefined, 1, 200), staleTime: 30_000 });
  const parties = partiesResult?.data ?? [];
  const { data: productsResult } = useQuery({ queryKey: ['products'], queryFn: () => productsApi.list(undefined, 1, 200), staleTime: 30_000 });
  const products = productsResult?.data ?? [];
  const { data: lorryReceiptsResult } = useQuery({
    queryKey: ['lorry-receipts-for-import'],
    queryFn: () => invoicesApi.list({ docType: 'lorry_receipt' }, 1, 200),
    enabled: urlDocType !== 'lorry_receipt',
    staleTime: 30_000,
  });
  const lorryReceipts = (lorryReceiptsResult?.data ?? []).filter(lr => lr.lrStatus !== 'billed');
  const { data: salesInvoicesForCnResult } = useQuery({
    queryKey: ['invoices-for-cn'],
    queryFn: () => invoicesApi.list({}, 1, 200),
    enabled: false, // loaded on demand below
    staleTime: 30_000,
  });
  const { data: purchasesForDnResult } = useQuery({
    queryKey: ['purchases-for-dn'],
    queryFn: () => purchasesApi.list(1, 200),
    enabled: false,
    staleTime: 30_000,
  });
  const { data: sequences = [] } = useQuery({ queryKey: ['sequences'], queryFn: companyApi.getSequences, staleTime: 5 * 60_000, enabled: !isEdit });

  const [form, setForm] = useState<Partial<Invoice>>({
    date: today(),
    docType: urlDocType ?? 'tax_invoice',
    taxType: 'cgst_sgst', status: 'unpaid',
    items: [{ id: Math.random().toString(36).slice(2), description: '', qty: 1, rate: 0, taxableValue: 0, gstRate: 18, total: 0 }],
    currency: 'INR',
    ...(urlPartyId ? { partyId: urlPartyId } : {}),
    ...(urlPartyName ? { partyName: urlPartyName } : {}),
    ...(urlLrRefs ? { lrRefs: urlLrRefs } : {}),
    ...(templateData ? { ...templateData, date: today(), invoiceNumber: '', status: 'unpaid' } : {}),
  });
  const [partySearch, setPartySearch] = useState(templateData?.partyName ?? urlPartyName ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const savedRef = useRef(false);
  const [showLrImport, setShowLrImport] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [importedLrIds, setImportedLrIds] = useState<string[]>([]);

  const nextNumberPreview = (() => {
    if (isEdit) return '';
    const docTypeMap: Record<string, string> = { tax_invoice: 'TAX_INVOICE', bill_of_supply: 'BILL_OF_SUPPLY', quotation: 'QUOTATION', credit_note: 'CREDIT_NOTE', debit_note: 'DEBIT_NOTE', lorry_receipt: 'LORRY_RECEIPT' };
    const seq = sequences.find(s => s.docType === (docTypeMap[form.docType ?? 'tax_invoice'] ?? 'TAX_INVOICE'));
    if (!seq) return 'Auto-generated';
    const yr = new Date().getFullYear(); const m = new Date().getMonth() + 1;
    const fy = m >= 4 ? `${yr}-${String(yr + 1).slice(2)}` : `${yr - 1}-${String(yr).slice(2)}`;
    return `${seq.prefix}/${fy}/${String(seq.lastNumber + 1).padStart(4, '0')}`;
  })();

  useEffect(() => {
    if (existing) { setForm(existing); setPartySearch(existing.partyName ?? ''); }
  }, [existing]);

  const formInitialized = useRef(false);
  useEffect(() => {
    if (!formInitialized.current) { formInitialized.current = true; return; }
    if (!savedRef.current) setIsDirty(true);
  }, [form]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Lazy-load CN/DN reference data only when the relevant docType is selected
  useEffect(() => {
    if (form.docType === 'credit_note') qc.prefetchQuery({ queryKey: ['invoices-for-cn'], queryFn: () => invoicesApi.list({}, 1, 200), staleTime: 30_000 });
    if (form.docType === 'debit_note') qc.prefetchQuery({ queryKey: ['purchases-for-dn'], queryFn: () => purchasesApi.list(1, 200), staleTime: 30_000 });
  }, [form.docType, qc]);

  const salesInvoicesForCn = salesInvoicesForCnResult?.data ?? [];
  // Only real Purchase Invoices belong here — the unfiltered list also includes purchase-side
  // Debit Notes (purchase returns), which aren't something a new Debit Note should be raised against.
  const purchasesForDn = (purchasesForDnResult?.data ?? []).filter(p => p.docType === 'purchase_invoice');

  async function guardedNavigate(to: string) {
    if (isDirty && !savedRef.current && !await confirm({ message: 'You have unsaved changes. Leave anyway?', confirmLabel: 'Leave' })) return;
    navigate(to);
  }

  const set = useCallback((k: keyof Invoice) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    setForm(f => {
      const update: Partial<Invoice> = { [k]: val };
      if (k === 'date' && val) {
        const d = new Date(val); d.setDate(d.getDate() + 30);
        update.dueDate = d.toISOString().slice(0, 10);
      }
      return { ...f, ...update };
    });
  }, []);

  const setNum = useCallback((k: keyof Invoice) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [k]: parseFloat(e.target.value) || 0 }));
  }, []);

  const setBool = useCallback((k: keyof Invoice) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [k]: e.target.checked }));
  }, []);

  async function handleSave() {
    if (!form.partyName?.trim()) { setError('Party name is required'); return; }
    if (!form.date) { setError('Date is required'); return; }
    if (!form.items?.length) { setError('Add at least one item'); return; }
    setError(null);
    setSaving(true);
    try {
      if (isEdit) { await invoicesApi.update(id!, form); }
      else { await invoicesApi.create(form); }
      if (importedLrIds.length > 0) {
        await Promise.all(importedLrIds.map(lrId => invoicesApi.update(lrId, { lrStatus: 'billed' })));
        qc.invalidateQueries({ queryKey: ['lorry-receipts-for-import'] });
      }
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.refetchQueries({ queryKey: ['sales-summary'], type: 'all' });
      qc.refetchQueries({ queryKey: ['outstanding'], type: 'all' });
      qc.refetchQueries({ type: 'all' });
      savedRef.current = true;
      navigate('/invoices');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally { setSaving(false); }
  }

  if (isEdit && loadingInv) return <div className="loading-overlay"><span className="spinner" /></div>;

  const subtotal = form.subtotal ?? 0;
  const taxTotal = form.taxTotal ?? 0;
  const tdsAmt = form.tdsRate ? (subtotal * (form.tdsRate ?? 0) / 100) : 0;
  const tcsAmt = form.tcsRate ? (subtotal * (form.tcsRate ?? 0) / 100) : 0;
  const advanceAmt = form.advanceReceived ?? 0;
  const netTotal = subtotal + taxTotal - tdsAmt + tcsAmt;
  const isCgst = form.taxType === 'cgst_sgst';
  const isLr = form.docType === 'lorry_receipt';

  return (
    <>
    <div className="page" style={{ maxWidth: 1100 }}>
      <div className="page-header">
        <h1 className="page-title">{isEdit ? 'Edit Invoice' : 'New Invoice'}</h1>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={() => guardedNavigate(isEdit ? `/invoices/${id}` : '/invoices')}>Cancel</button>
          {isTransport && form.docType === 'tax_invoice' && (
            <button className="btn btn-ghost" onClick={() => setShowLrImport(true)}>📦 Import from LRs</button>
          )}
          <button className="btn btn-ghost" onClick={() => {
            const name = prompt('Template name:', form.partyName ? `${form.partyName} — ${form.docType ?? 'invoice'}` : 'My Template');
            if (!name?.trim()) return;
            const desc = prompt('Short description (optional):') ?? '';
            saveTemplate({ id: crypto.randomUUID(), name: name.trim(), description: desc, invoiceData: form, createdAt: new Date().toISOString() });
            alert('Template saved!');
          }}>Save as Template</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : (isEdit ? 'Save Changes' : 'Create Invoice')}
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* ── Invoice Details ───────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Invoice Details</span></div>
        <div className="card-body">
          <div className="form-row form-row-5">
            <label>Doc Type
              <select value={form.docType ?? 'tax_invoice'} onChange={set('docType')}>
                <option value="tax_invoice">Tax Invoice</option>
                <option value="bill_of_supply">Bill of Supply</option>
                <option value="quotation" disabled={!!trialDocTypes && !trialDocTypes.has('quotation')} title={trialDocTypes ? 'Upgrade to use Quotation' : undefined}>Quotation{trialDocTypes ? ' 🔒' : ''}</option>
                <option value="credit_note" disabled={!!trialDocTypes && !trialDocTypes.has('credit_note')} title={trialDocTypes ? 'Upgrade to use Credit Note' : undefined}>Credit Note{trialDocTypes ? ' 🔒' : ''}</option>
                <option value="debit_note" disabled={!!trialDocTypes && !trialDocTypes.has('debit_note')} title={trialDocTypes ? 'Upgrade to use Debit Note' : undefined}>Debit Note{trialDocTypes ? ' 🔒' : ''}</option>
                {isTransport && <option value="lorry_receipt">Lorry Receipt</option>}
              </select>
            </label>
            <label>{isLr ? 'LR # (blank = auto)' : 'Invoice # (blank = auto)'}
              <input value={form.invoiceNumber ?? ''} onChange={set('invoiceNumber')} placeholder={nextNumberPreview} />
            </label>
            <label>Date *<input type="date" value={form.date ?? ''} onChange={set('date')} /></label>
            {!isLr && <label>Due Date<input type="date" value={form.dueDate ?? ''} onChange={set('dueDate')} /></label>}
            <label>GST Type
              <select value={form.taxType ?? 'cgst_sgst'} onChange={set('taxType')}>
                <option value="cgst_sgst">CGST + SGST (Intra-state)</option>
                <option value="igst">IGST (Inter-state)</option>
              </select>
            </label>
          </div>
          <div className="form-row form-row-3">
            <label>Place of Supply
              <select value={form.placeOfSupply ?? ''} onChange={set('placeOfSupply')}>
                <option value="">— Select state —</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>
              {(form.docType === 'credit_note' || form.docType === 'debit_note') ? 'Invoice Reference (auto-filled)' : 'Reference #'}
              <input value={form.referenceNumber ?? ''} onChange={set('referenceNumber')} placeholder={(form.docType === 'credit_note' || form.docType === 'debit_note') ? 'Original invoice number' : 'PO number, etc.'} />
            </label>
            <label>Currency
              <select value={form.currency ?? 'INR'} onChange={set('currency')}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          {form.currency && form.currency !== 'INR' && (
            <div className="form-row" style={{ maxWidth: 280 }}>
              <label>Exchange Rate (1 {form.currency} = ₹)
                <input type="number" min={0} step={0.01} value={form.exchangeRate ?? ''} onChange={setNum('exchangeRate')} placeholder="e.g. 83.5" />
              </label>
            </div>
          )}
          {form.docType === 'credit_note' && (
            <div className="form-row" style={{ maxWidth: 480 }}>
              <label>Original Sales Invoice this Credit Note is against
                <select value={form.linkedInvoiceId ?? ''} onChange={e => {
                  const inv = salesInvoicesForCn.find(i => i.id === e.target.value);
                  setForm(f => ({
                    ...f, linkedInvoiceId: e.target.value || undefined,
                    referenceNumber: inv?.invoiceNumber ?? f.referenceNumber,
                    ...(inv ? { partyId: inv.partyId, partyName: inv.partyName, partyGstin: inv.partyGstin, partyAddress: inv.partyAddress } : {}),
                  }));
                  if (inv) setPartySearch(inv.partyName);
                }}>
                  <option value="">— Select sales invoice (optional) —</option>
                  {[...salesInvoicesForCn].filter(i => i.docType !== 'credit_note' && i.docType !== 'lorry_receipt' && i.docType !== 'quotation').sort((a, b) => b.date.localeCompare(a.date)).map(i => (
                    <option key={i.id} value={i.id}>{i.invoiceNumber ?? i.id.slice(0, 8)} — {i.partyName} ({i.date})</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {form.docType === 'debit_note' && (
            <div className="form-row" style={{ maxWidth: 480 }}>
              <label>Original Purchase Invoice this Debit Note is against
                <select value={form.linkedInvoiceId ?? ''} onChange={e => {
                  const pi = purchasesForDn.find(p => p.id === e.target.value);
                  setForm(f => ({
                    ...f, linkedInvoiceId: e.target.value || undefined,
                    referenceNumber: pi?.invoiceNumber ?? f.referenceNumber,
                    ...(pi ? { partyId: pi.partyId, partyName: pi.partyName, partyGstin: pi.partyGstin } : {}),
                  }));
                  if (pi) setPartySearch(pi.partyName);
                }}>
                  <option value="">— Select purchase invoice (optional) —</option>
                  {[...purchasesForDn].sort((a, b) => b.date.localeCompare(a.date)).map(p => (
                    <option key={p.id} value={p.id}>{p.invoiceNumber ?? p.id.slice(0, 8)} — {p.partyName} ({p.date})</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 'normal', cursor: 'pointer', fontSize: '0.875rem' }}>
              <input type="checkbox" checked={form.isRcm ?? false} onChange={setBool('isRcm')} style={{ width: 'auto' }} />
              Reverse Charge (RCM)
            </label>
          </div>
        </div>
      </div>

      {/* ── Party ─────────────────────────────────────────── */}
      <PartySelector
        partySearch={partySearch}
        onSearchChange={val => { setPartySearch(val); setForm(f => ({ ...f, partyName: val, partyId: undefined })); }}
        parties={parties}
        onSelectParty={p => {
          setForm(f => ({
            ...f, partyId: p.id, partyName: p.name, partyGstin: p.gstin,
            partyAddress: [p.addressLine1, p.city, p.state].filter(Boolean).join(', '),
            isRcm: p.isRcm,
            taxType: p.state ? autoTaxType((f as { profileState?: string }).profileState, p.state) : f.taxType,
          }));
          setPartySearch(p.name);
        }}
        partyGstin={form.partyGstin ?? ''}
        onGstinChange={val => setForm(f => ({ ...f, partyGstin: val }))}
        partyAddress={form.partyAddress ?? ''}
        onAddressChange={val => setForm(f => ({ ...f, partyAddress: val }))}
        docType={form.docType ?? 'tax_invoice'}
        consigneeName={form.consigneeName}
        consigneeGstin={form.consigneeGstin}
        consigneeAddress={form.consigneeAddress}
        onConsigneeChange={(k, val) => setForm(f => ({ ...f, [k]: val }))}
      />

      {/* ── Line Items ────────────────────────────────────── */}
      <InvoiceItemsTable
        items={form.items ?? []}
        products={products}
        totals={{ subtotal, taxTotal, tdsAmt, tcsAmt, advanceAmt, netTotal, isCgst, tdsRate: form.tdsRate, tdsSection: form.tdsSection, tcsRate: form.tcsRate }}
        onItemsChange={items => setForm(f => ({ ...f, items, ...recalcTotals(items) }))}
        onOpenProductPicker={() => setShowProductPicker(true)}
      />

      {/* ── Tax Adjustments ───────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Tax Adjustments</span></div>
        <div className="card-body">
          <div className="form-row form-row-3">
            <label>TDS Section
              <select value={form.tdsSection ?? ''} onChange={e => {
                const sec = e.target.value;
                const info = TDS_SECTIONS.find(s => s.section === sec);
                setForm(f => ({ ...f, tdsSection: sec || undefined, ...(info ? { tdsRate: info.rate } : {}) }));
              }}>
                <option value="">— None —</option>
                {TDS_SECTIONS.map(s => <option key={s.section} value={s.section}>{s.label}</option>)}
              </select>
            </label>
            <label>TDS Rate (%)
              <input type="number" min={0} max={30} step={0.1} value={form.tdsRate ?? ''} onChange={e => setForm(f => ({ ...f, tdsRate: parseFloat(e.target.value) || undefined }))} placeholder="Auto-filled from section" />
            </label>
            <label>TCS Rate (%)
              <input type="number" min={0} max={5} step={0.1} value={form.tcsRate ?? ''} onChange={e => setForm(f => ({ ...f, tcsRate: parseFloat(e.target.value) || undefined }))} placeholder="e.g. 0.1" />
            </label>
          </div>
          <div className="form-row form-row-2">
            <label>Advance Received (₹)
              <input type="number" min={0} step={0.01} value={form.advanceReceived ?? ''} onChange={e => setForm(f => ({ ...f, advanceReceived: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
            </label>
            <label>Advance Mode
              <select value={form.advanceMode ?? ''} onChange={e => setForm(f => ({ ...f, advanceMode: e.target.value as PaymentMode || undefined }))}>
                <option value="">— None —</option>
                {PAYMENT_MODES.map(m => <option key={m} value={m} style={{ textTransform: 'capitalize' }}>{m.replace('_', ' ')}</option>)}
              </select>
            </label>
          </div>
        </div>
      </div>

      {/* ── Transport / LR ────────────────────────────────── */}
      {(isLr || form.vehicleNumber || form.lrNumber || form.fromCity) ? (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><span style={{ fontWeight: 700 }}>Transport Details</span></div>
          <div className="card-body">
            <div className="form-row form-row-3">
              <label>Vehicle Number<input value={form.vehicleNumber ?? ''} onChange={e => setForm(f => ({ ...f, vehicleNumber: e.target.value.toUpperCase() }))} placeholder="MH12AB1234" /></label>
              <label>LR Number<input value={form.lrNumber ?? ''} onChange={set('lrNumber')} placeholder="LR-001" /></label>
              <label>Driver Name<input value={form.driverName ?? ''} onChange={set('driverName')} placeholder="Driver full name" /></label>
            </div>
            <div className="form-row form-row-2">
              <label>From City<input value={form.fromCity ?? ''} onChange={set('fromCity')} placeholder="Mumbai" /></label>
              <label>To City<input value={form.toCity ?? ''} onChange={set('toCity')} placeholder="Delhi" /></label>
            </div>
            {isLr && (
              <div className="form-row">
                <label>LR Refs (linked invoice LR numbers, comma-separated)
                  <input value={form.lrRefs ?? ''} onChange={set('lrRefs')} placeholder="LR-001, LR-002, LR-003" />
                </label>
              </div>
            )}
            {isLr && (
              <TripsEditor
                trips={form.trips ?? []}
                onChange={trips => setForm(f => ({ ...f, trips }))}
              />
            )}
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: '1rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setForm(f => ({ ...f, vehicleNumber: '' }))}>
            + Add Transport / LR Details
          </button>
        </div>
      )}

      {/* ── Notes / Terms ─────────────────────────────────── */}
      <div className="card">
        <div className="card-header"><span style={{ fontWeight: 700 }}>Notes & Terms</span></div>
        <div className="card-body">
          <label>Terms & Conditions / Notes
            <textarea value={form.termsAndConditions ?? ''} onChange={e => setForm(f => ({ ...f, termsAndConditions: e.target.value }))} rows={3} placeholder="Payment due within 30 days…" />
          </label>
        </div>
      </div>
    </div>

    <LrImportModal
      show={showLrImport}
      onClose={() => setShowLrImport(false)}
      lorryReceipts={lorryReceipts}
      onImport={selected => {
        const baseItems = (form.items ?? []).filter(it => it.description || it.rate > 0);
        const mergedItems = [...baseItems, ...selected.flatMap(lr => lr.items ?? [])];
        const mergedTrips = [...(form.trips ?? []), ...selected.flatMap(lr => lr.trips ?? [])];
        const lrNums = selected.map(lr => lr.invoiceNumber).filter(Boolean) as string[];
        const existing = form.lrRefs?.split(',').map(s => s.trim()).filter(Boolean) ?? [];
        const lrRefs = [...new Set([...existing, ...lrNums])].join(', ');
        const items = mergedItems.length ? mergedItems : [{ id: Math.random().toString(36).slice(2), description: '', qty: 1, rate: 0, taxableValue: 0, gstRate: 18, total: 0 }];
        setImportedLrIds(prev => [...new Set([...prev, ...selected.map(lr => lr.id)])]);
        setForm(f => ({ ...f, items, trips: mergedTrips.length ? mergedTrips : f.trips, lrRefs, ...recalcTotals(items) }));
      }}
    />

    <ProductPickerModal
      show={showProductPicker}
      onClose={() => setShowProductPicker(false)}
      products={products}
      onAddProduct={p => {
        const id = Math.random().toString(36).slice(2);
        const raw = { id, productId: p.id, description: p.name, hsnSac: p.hsnSac, rate: p.rate, gstRate: p.gstRate, qty: 1, taxableValue: 0, total: 0 };
        const { taxableValue, total } = calcItemTotals(raw);
        const item = { ...raw, taxableValue, total };
        setForm(f => { const items = [...(f.items ?? []), item]; return { ...f, items, ...recalcTotals(items) }; });
      }}
      onCreateProduct={async data => {
        const created = await productsApi.create({ ...data, type: 'product' });
        qc.invalidateQueries({ queryKey: ['products'] });
        qc.refetchQueries({ type: 'all' });
        return created as Product;
      }}
    />
    </>
  );
}
